import { Injectable } from '@angular/core';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { IAccount, IVisitor, NgxPendoService } from 'ngx-pendo';
import { UserModel } from '@portal/app/shared/models/userModel';
import { environment } from '@portal/environments/environment';

interface PendoGlobal {
  isReady?: () => boolean;
}

declare global {
  interface Window {
    pendo?: PendoGlobal;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  private static readonly DEFAULT_ACCOUNT_ID = '1000';
  private static readonly DEFAULT_ACCOUNT_NAME = 'Measured';

  constructor(
    private readonly selectionService: SelectionService,
    private readonly pendoService: NgxPendoService
  ) {}

  /**
   * Sets up Pendo with the current user context.
   * Initializes Pendo if not already initialized, then identifies the user.
   */
  public setupPendo(user: UserModel): void {
    if (!environment.production) {
      return;
    }

    const accountInfo = this.createAccountInfo();

    try {
      if (!this.isPendoInitialized()) {
        this.initializePendoWithUser(user, accountInfo);
      } else {
        this.identifyPendo(user, accountInfo);
      }
    } catch (error) {
      console.error('Pendo setup failed:', error);
    }
  }

  /**
   * Loads Pendo anonymously (no visitor/account) for pre-auth pages like /login.
   * After login, setupPendo() detects the initialized agent and calls identify().
   */
  public initializeAnonymous(): void {
    if (!environment.production) {
      return;
    }

    if (this.isPendoInitialized()) {
      return;
    }

    try {
      this.pendoService.initialize({});
    } catch (error) {
      console.error('Pendo anonymous init failed:', error);
    }
  }

  /**
   * Initializes Pendo with visitor and account information.
   */
  private initializePendoWithUser(
    user: UserModel,
    accountInfo: IAccount
  ): void {
    const visitorInfo = this.getVisitorInfo(user);
    if (!visitorInfo) {
      return;
    }

    this.pendoService.initialize(visitorInfo, accountInfo);
  }

  /**
   * Identifies the user in Pendo with current visitor and account information.
   */
  private identifyPendo(user: UserModel, accountInfo: IAccount): void {
    const visitorInfo = this.getVisitorInfo(user);

    if (!visitorInfo) {
      console.warn('Pendo identification aborted: Missing visitor information');
      return;
    }

    this.pendoService.identify(visitorInfo, accountInfo);
  }

  /**
   * Gets visitor information from the user model.
   */
  private getVisitorInfo(user: UserModel): IVisitor | null {
    const visitorInfo = user.userDetails(this.selectionService);

    if (!visitorInfo?.email) {
      console.warn('Invalid visitor info: Missing email address');
      return null;
    }

    return visitorInfo;
  }

  /**
   * Creates account information from the current brand selection.
   */
  private createAccountInfo(): IAccount {
    const { brandId, brandName } = this.selectionService.clientBrandSignal();
    const accountId = brandId
      ? String(brandId)
      : PendoService.DEFAULT_ACCOUNT_ID;
    const accountName = brandName || PendoService.DEFAULT_ACCOUNT_NAME;

    return {
      id: accountId,
      name: accountName,
      brandId: accountId,
      brandName: accountName
    };
  }

  /**
   * Checks if Pendo is already initialized and ready.
   */
  private isPendoInitialized(): boolean {
    try {
      return !!(window.pendo?.isReady && window.pendo.isReady());
    } catch (error) {
      console.warn('Error checking Pendo initialization status:', error);
      return false;
    }
  }
}
